import {
  CustomAnalyticsQueryDatePartSchema,
  type CustomAnalyticsQueryTimeUnit,
} from "../shared/custom-analytics-query-primitives";
import type { CustomAnalyticsQueryPlanFunctionName } from "./custom-analytics-query-plan-primitives";

export interface CustomAnalyticsQueryIntegerBounds {
  readonly min: number;
  readonly max: number;
}

export const ANALYTICS_SCALAR_POSITION_BOUNDS = {
  min: 1,
  max: 100,
} as const satisfies CustomAnalyticsQueryIntegerBounds;

export const ANALYTICS_SCALAR_LENGTH_BOUNDS = {
  min: 1,
  max: 10_000,
} as const satisfies CustomAnalyticsQueryIntegerBounds;

export const ANALYTICS_SCALAR_ROUND_DIGIT_BOUNDS = {
  min: -6,
  max: 12,
} as const satisfies CustomAnalyticsQueryIntegerBounds;

export const ANALYTICS_SCALAR_MODULUS_BOUNDS = {
  min: 1,
  max: 1_000_000,
} as const satisfies CustomAnalyticsQueryIntegerBounds;

export const ANALYTICS_SCALAR_DATE_OFFSET_BOUNDS = {
  min: -3650,
  max: 3650,
} as const satisfies CustomAnalyticsQueryIntegerBounds;

export const ANALYTICS_SCALAR_PAD_BOUNDS = {
  min: 1,
  max: 1000,
} as const satisfies CustomAnalyticsQueryIntegerBounds;

const CUSTOM_ANALYTICS_QUERY_INTERVAL_UNITS = [
  "hour",
  "day",
  "week",
  "month",
  "quarter",
  "year",
] as const satisfies readonly CustomAnalyticsQueryTimeUnit[];

const CUSTOM_ANALYTICS_QUERY_COMPONENT_UNITS = [
  "hour",
  "day",
  "dayofyear",
  "iso_dayofweek",
  "iso_week",
  "month",
  "quarter",
  "year",
] as const satisfies readonly CustomAnalyticsQueryTimeUnit[];

export const CUSTOM_ANALYTICS_QUERY_SUB_DAY_UNITS: readonly CustomAnalyticsQueryTimeUnit[] =
  ["hour"];

export type CustomAnalyticsQueryArgKind =
  | "text"
  | "date"
  | "datetime"
  | "date_by_unit"
  | "numeric"
  | "plain_number";

export type CustomAnalyticsQueryArgSpec =
  | { readonly gate: "typed"; readonly kind: CustomAnalyticsQueryArgKind }
  | {
      readonly gate: "integer_literal";
      readonly bounds: CustomAnalyticsQueryIntegerBounds;
    }
  | { readonly gate: "uniform" }
  | { readonly gate: "uniform_non_null" };

export type CustomAnalyticsQueryResultValueType =
  | "text"
  | "number"
  | "date"
  | "datetime";

export type CustomAnalyticsQueryResultRule =
  | {
      readonly kind: "fixed";
      readonly valueType: CustomAnalyticsQueryResultValueType;
    }
  | { readonly kind: "arg"; readonly index: number }
  | { readonly kind: "uniform" };

export type CustomAnalyticsQueryReportLocalRule =
  | { readonly mode: "args" }
  | { readonly mode: "self"; readonly wrap: "date" | "timestamp" }
  | null;

export interface CustomAnalyticsQueryFunctionHelpParts {
  readonly args: readonly string[];
  readonly unit: string;
}

export interface CustomAnalyticsQueryPlanOnlySignature {
  readonly exposure: "plan_only";
  readonly requiredArgs: number;
  readonly maxArgs: number;
  readonly units: readonly CustomAnalyticsQueryTimeUnit[] | null;
}

export interface CustomAnalyticsQueryAstSignature {
  readonly exposure: "ast";
  readonly astName: CustomAnalyticsQueryPlanFunctionName;
  readonly astAliases?: readonly string[];
  readonly args: readonly CustomAnalyticsQueryArgSpec[];
  readonly rest?: CustomAnalyticsQueryArgSpec;
  readonly requiredArgs: number;
  readonly maxArgs: number;
  readonly planRequiredArgs?: number;
  readonly units: readonly CustomAnalyticsQueryTimeUnit[] | null;
  readonly result: CustomAnalyticsQueryResultRule;
  readonly reportLocal: CustomAnalyticsQueryReportLocalRule;
  readonly help: (parts: CustomAnalyticsQueryFunctionHelpParts) => string;
}

export type CustomAnalyticsQueryFunctionSignature =
  | CustomAnalyticsQueryPlanOnlySignature
  | CustomAnalyticsQueryAstSignature;

const TEXT_ARG = { gate: "typed", kind: "text" } as const;
const NUMERIC_ARG = { gate: "typed", kind: "numeric" } as const;
const UNIFORM_ARG = { gate: "uniform" } as const;
const UNIFORM_NON_NULL_ARG = { gate: "uniform_non_null" } as const;

export const CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES = {
  abs: {
    exposure: "ast",
    astName: "abs",
    args: [NUMERIC_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "arg", index: 0 },
    reportLocal: null,
    help: ({ args }) => `${args[0]} without its sign`,
  },
  array_contains: {
    exposure: "plan_only",
    requiredArgs: 2,
    maxArgs: 2,
    units: null,
  },
  cardinality: {
    exposure: "plan_only",
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
  },
  ceil: {
    exposure: "ast",
    astName: "ceil",
    args: [NUMERIC_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "arg", index: 0 },
    reportLocal: null,
    help: ({ args }) => `${args[0]} rounded up to a whole number`,
  },
  char_length: {
    exposure: "ast",
    astName: "char_length",
    args: [TEXT_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "fixed", valueType: "number" },
    reportLocal: null,
    help: ({ args }) => `the number of characters in ${args[0]}`,
  },
  coalesce: {
    exposure: "ast",
    astName: "coalesce",
    args: [UNIFORM_ARG, UNIFORM_ARG],
    rest: UNIFORM_ARG,
    requiredArgs: 2,
    maxArgs: 8,
    planRequiredArgs: 1,
    units: null,
    result: { kind: "uniform" },
    reportLocal: null,
    help: ({ args }) =>
      `${args.slice(0, -1).join(", ")}, falling back to ${args[args.length - 1]}`,
  },
  concat: {
    exposure: "ast",
    astName: "concat",
    args: [TEXT_ARG, TEXT_ARG],
    rest: TEXT_ARG,
    requiredArgs: 2,
    maxArgs: 8,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) => args.join(" joined with "),
  },
  current_date: {
    exposure: "ast",
    astName: "current_date",
    args: [],
    requiredArgs: 0,
    maxArgs: 0,
    units: null,
    result: { kind: "fixed", valueType: "date" },
    reportLocal: { mode: "self", wrap: "date" },
    help: () => "today's date in the report's time zone",
  },
  current_timestamp: {
    exposure: "ast",
    astName: "current_timestamp",
    args: [],
    requiredArgs: 0,
    maxArgs: 0,
    units: null,
    result: { kind: "fixed", valueType: "datetime" },
    reportLocal: { mode: "self", wrap: "timestamp" },
    help: () => "the current time in the report's time zone",
  },
  current_utc_timestamp: {
    exposure: "plan_only",
    requiredArgs: 0,
    maxArgs: 0,
    units: null,
  },
  date_add: {
    exposure: "ast",
    astName: "date_add",
    args: [
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_DATE_OFFSET_BOUNDS },
      { gate: "typed", kind: "date_by_unit" },
    ],
    requiredArgs: 2,
    maxArgs: 2,
    units: CustomAnalyticsQueryDatePartSchema.options,
    result: { kind: "arg", index: 1 },
    reportLocal: { mode: "args" },
    help: ({ args, unit }) => `${args[1]} shifted by ${args[0]} ${unit}s`,
  },
  date_diff: {
    exposure: "ast",
    astName: "date_diff",
    args: [
      { gate: "typed", kind: "date_by_unit" },
      { gate: "typed", kind: "date_by_unit" },
    ],
    requiredArgs: 2,
    maxArgs: 2,
    units: CUSTOM_ANALYTICS_QUERY_INTERVAL_UNITS,
    result: { kind: "fixed", valueType: "number" },
    reportLocal: { mode: "args" },
    help: ({ args, unit }) =>
      `the number of ${unit}s from ${args[0]} to ${args[1]}`,
  },
  date_trunc: {
    exposure: "ast",
    astName: "date_trunc",
    astAliases: ["date_floor"],
    args: [{ gate: "typed", kind: "date" }],
    requiredArgs: 1,
    maxArgs: 1,
    units: CustomAnalyticsQueryDatePartSchema.options,
    result: { kind: "fixed", valueType: "date" },
    reportLocal: { mode: "args" },
    help: ({ args, unit }) =>
      `${args[0]} rounded down to the start of its ${unit}`,
  },
  divide_or_zero: {
    exposure: "plan_only",
    requiredArgs: 2,
    maxArgs: 2,
    units: null,
  },
  extract: {
    exposure: "ast",
    astName: "extract",
    args: [{ gate: "typed", kind: "date_by_unit" }],
    requiredArgs: 1,
    maxArgs: 1,
    units: CUSTOM_ANALYTICS_QUERY_COMPONENT_UNITS,
    result: { kind: "fixed", valueType: "number" },
    reportLocal: { mode: "args" },
    help: ({ args, unit }) => `the ${unit} of ${args[0]}`,
  },
  floor: {
    exposure: "ast",
    astName: "floor",
    args: [NUMERIC_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "arg", index: 0 },
    reportLocal: null,
    help: ({ args }) => `${args[0]} rounded down to a whole number`,
  },
  greatest: {
    exposure: "ast",
    astName: "greatest",
    args: [UNIFORM_NON_NULL_ARG, UNIFORM_NON_NULL_ARG],
    rest: UNIFORM_NON_NULL_ARG,
    requiredArgs: 2,
    maxArgs: 8,
    units: null,
    result: { kind: "uniform" },
    reportLocal: null,
    help: ({ args }) => `the largest of ${args.join(", ")}`,
  },
  if: { exposure: "plan_only", requiredArgs: 3, maxArgs: 3, units: null },
  last_day: {
    exposure: "ast",
    astName: "last_day",
    args: [{ gate: "typed", kind: "date" }],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "fixed", valueType: "date" },
    reportLocal: { mode: "args" },
    help: ({ args }) => `the last day of the month containing ${args[0]}`,
  },
  least: {
    exposure: "ast",
    astName: "least",
    args: [UNIFORM_NON_NULL_ARG, UNIFORM_NON_NULL_ARG],
    rest: UNIFORM_NON_NULL_ARG,
    requiredArgs: 2,
    maxArgs: 8,
    units: null,
    result: { kind: "uniform" },
    reportLocal: null,
    help: ({ args }) => `the smallest of ${args.join(", ")}`,
  },
  lower: {
    exposure: "ast",
    astName: "lower",
    args: [TEXT_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) => `${args[0]} in lower case`,
  },
  lpad: {
    exposure: "ast",
    astName: "lpad",
    args: [
      TEXT_ARG,
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_PAD_BOUNDS },
      TEXT_ARG,
    ],
    requiredArgs: 3,
    maxArgs: 3,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) =>
      `${args[0]} padded on the left with ${args[2]} to ${args[1]} characters`,
  },
  mod: {
    exposure: "ast",
    astName: "mod",
    args: [
      NUMERIC_ARG,
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_MODULUS_BOUNDS },
    ],
    requiredArgs: 2,
    maxArgs: 2,
    units: null,
    result: { kind: "arg", index: 0 },
    reportLocal: null,
    help: ({ args }) => `the remainder of ${args[0]} divided by ${args[1]}`,
  },
  nullif: {
    exposure: "ast",
    astName: "nullif",
    args: [UNIFORM_ARG, UNIFORM_ARG],
    requiredArgs: 2,
    maxArgs: 2,
    units: null,
    result: { kind: "uniform" },
    reportLocal: null,
    help: ({ args }) => `${args[0]}, or no value when it equals ${args[1]}`,
  },
  power: {
    exposure: "ast",
    astName: "power",
    args: [
      { gate: "typed", kind: "plain_number" },
      { gate: "typed", kind: "plain_number" },
    ],
    requiredArgs: 2,
    maxArgs: 2,
    units: null,
    result: { kind: "fixed", valueType: "number" },
    reportLocal: null,
    help: ({ args }) => `${args[0]} raised to the power of ${args[1]}`,
  },
  primary_return_reason: {
    exposure: "plan_only",
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
  },
  replace: {
    exposure: "ast",
    astName: "replace",
    args: [TEXT_ARG, TEXT_ARG, TEXT_ARG],
    requiredArgs: 3,
    maxArgs: 3,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) =>
      `${args[0]} with every ${args[1]} replaced by ${args[2]}`,
  },
  report_local_date: {
    exposure: "plan_only",
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
  },
  report_local_timestamp: {
    exposure: "plan_only",
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
  },
  return_input_answer_comments: {
    exposure: "plan_only",
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
  },
  right: {
    exposure: "ast",
    astName: "right",
    args: [
      TEXT_ARG,
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_PAD_BOUNDS },
    ],
    requiredArgs: 2,
    maxArgs: 2,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) => `the last ${args[1]} characters of ${args[0]}`,
  },
  round: {
    exposure: "ast",
    astName: "round",
    args: [
      NUMERIC_ARG,
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_ROUND_DIGIT_BOUNDS },
    ],
    requiredArgs: 1,
    maxArgs: 2,
    units: null,
    result: { kind: "arg", index: 0 },
    reportLocal: null,
    help: ({ args }) =>
      args[1]
        ? `${args[0]} rounded to ${args[1]} decimal places`
        : `${args[0]} rounded to a whole number`,
  },
  rpad: {
    exposure: "ast",
    astName: "rpad",
    args: [
      TEXT_ARG,
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_PAD_BOUNDS },
      TEXT_ARG,
    ],
    requiredArgs: 3,
    maxArgs: 3,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) =>
      `${args[0]} padded on the right with ${args[2]} to ${args[1]} characters`,
  },
  secondary_return_reason: {
    exposure: "plan_only",
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
  },
  sign: {
    exposure: "ast",
    astName: "sign",
    args: [NUMERIC_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "fixed", valueType: "number" },
    reportLocal: null,
    help: ({ args }) => `whether ${args[0]} is negative, zero or positive`,
  },
  split_part: {
    exposure: "ast",
    astName: "split_part",
    astAliases: ["split_nth"],
    args: [
      TEXT_ARG,
      TEXT_ARG,
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_POSITION_BOUNDS },
    ],
    requiredArgs: 3,
    maxArgs: 3,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) => `segment ${args[2]} of ${args[0]} split on ${args[1]}`,
  },
  substring: {
    exposure: "ast",
    astName: "substring",
    args: [
      TEXT_ARG,
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_LENGTH_BOUNDS },
      { gate: "integer_literal", bounds: ANALYTICS_SCALAR_LENGTH_BOUNDS },
    ],
    requiredArgs: 2,
    maxArgs: 3,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) =>
      args[2]
        ? `${args[2]} characters of ${args[0]} starting at ${args[1]}`
        : `${args[0]} from character ${args[1]}`,
  },
  trim: {
    exposure: "ast",
    astName: "trim",
    args: [TEXT_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) => `${args[0]} without surrounding spaces`,
  },
  upper: {
    exposure: "ast",
    astName: "upper",
    args: [TEXT_ARG],
    requiredArgs: 1,
    maxArgs: 1,
    units: null,
    result: { kind: "fixed", valueType: "text" },
    reportLocal: null,
    help: ({ args }) => `${args[0]} in upper case`,
  },
} as const satisfies Record<
  CustomAnalyticsQueryPlanFunctionName,
  CustomAnalyticsQueryFunctionSignature
>;

export type CustomAnalyticsQueryAstFunctionName = {
  [K in keyof typeof CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES]: (typeof CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES)[K] extends {
    readonly exposure: "ast";
  }
    ? K
    : never;
}[keyof typeof CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES];

export interface CustomAnalyticsQueryPlanFunctionArity {
  readonly min: number;
  readonly max: number;
}

export function customAnalyticsQueryPlanFunctionArity(
  fn: CustomAnalyticsQueryPlanFunctionName,
): CustomAnalyticsQueryPlanFunctionArity {
  const signature: CustomAnalyticsQueryFunctionSignature =
    CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES[fn];
  const unitSlot = signature.units === null ? 0 : 1;
  const required =
    signature.exposure === "ast" && signature.planRequiredArgs !== undefined
      ? signature.planRequiredArgs
      : signature.requiredArgs;
  return { min: required + unitSlot, max: signature.maxArgs + unitSlot };
}

export function customAnalyticsQueryPlanFunctionUnits(
  fn: CustomAnalyticsQueryPlanFunctionName,
): readonly CustomAnalyticsQueryTimeUnit[] | undefined {
  return CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES[fn].units ?? undefined;
}

export function analyticsScalarFunctionSignature(
  fn: CustomAnalyticsQueryAstFunctionName,
): CustomAnalyticsQueryAstSignature {
  return CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES[fn];
}

export function analyticsScalarFunctionAliases(): Readonly<
  Record<string, CustomAnalyticsQueryAstFunctionName>
> {
  const aliases: Record<string, CustomAnalyticsQueryAstFunctionName> = {};
  for (const signature of Object.values(
    CUSTOM_ANALYTICS_QUERY_FUNCTION_SIGNATURES,
  )) {
    if (signature.exposure !== "ast" || !("astAliases" in signature)) {
      continue;
    }
    for (const alias of signature.astAliases) {
      aliases[alias] = signature.astName;
    }
  }
  return aliases;
}
